import { useDispatch, useSelector } from "react-redux";
import { URLdecode, URLencode } from "../utils/url";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useRef } from "react";
import axios from "axios";
import fetchServer from "../controllers/fetchServer";
import { setPolicies } from "../redux/reducers/tour/insuranceSlice";
import { useInsuranceActions } from "./useInsuranceActions";
import { setLoadingModal } from "../redux/reducers/modalSlice";
import { configurations } from "../configurations";

export function useInsuranceApi() {
  const params = URLdecode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getActualMinAndMaxInsurancePrice, parseInsuranceBookingData } =
    useInsuranceActions();
  const cancelGetFlightPoliciesToken = useRef(null);
  const cancelOrdersToken = useRef(null);
  const { ticketType } = useSelector((state) => state.order);

  const parseFlightPolicyPayload = ({ data }) => {
    return {
      personInfo: {
        birthDate: data?.birthDate || "1990-10-20",
        firstName: data?.firstName || "yared",
        middleName: data?.middleName || "fire",
        lastName: data?.lastName || "terefe",
        email: data?.email || "admin-dembe@yopmail.com",
        phone: data?.phone || "08034635316",
        gender: data?.gender || "Male",
        title: data?.title || "Mr",
        city: data?.city || "Lagos",
        state: data?.state || "Lagos",
        passportNumber: data?.passportNumber || "18277872A4",
      },
      destinationCountry: data?.departure?.country,
      startDate: data?.startDate,
      endDate: data?.endDate,
      purposeOfTravel: data?.purposeOfTravel || "Leisure",
      isRoundTrip: data?.isRoundTrip || false,
      numberOfAdults: Number(data?.numberOfAdults) || 1,
      numberOfChildren: Number(data?.numberOfChildren) || 0,
      numberOfInfants: Number(data?.numberOfInfants) || 0,
      flights: data?.flights || [
        {
          departureDate: data?.startDate,
          arrivalDate: data?.endDate,
          flightNumber: data?.flightNumber || "---",
          pnr: data?.pnr || "---",
          airline: data?.airline || "--",
          departureAirport: data?.departure?.iata,
          arrivalAirport: data?.arrival?.iata,
          segmentIndex: 0,
        },
      ],
      baggageAllowance: data?.baggageAllowance,
    };
  };

  const removePassportPhoto = (res, order) => {
    const booking = res?.data?.data;
    let updatedData = {
      ...booking,
      purchasedInsurance: {
        ...booking?.purchasedInsurance,
        orderData: {
          ...booking?.purchasedInsurance?.orderData,
          data: {
            ...booking?.purchasedInsurance?.orderData?.data,
            personsInfo:
              booking?.purchasedInsurance?.orderData?.data?.personInfo?.map(
                (info) => {
                  return { ...info, passportPhoto: "" };
                }
              ),
          },
        },
      },
    };
    if (order) {
      updatedData = {
        ...booking,
        orderData: {
          ...booking?.orderData,
          data: {
            ...booking?.orderData?.data,
            personsInfo: booking?.orderData?.data?.personInfo?.map((info) => {
              return { ...info, passportPhoto: "" };
            }),
          },
        },
      };
    }
    return updatedData;
  };

  const getFlightPolicies = async ({ data, setLoading, callback }) => {
    const payload = parseFlightPolicyPayload({ data });
    // if(data?.baggageAllowance)
    setLoading && setLoading(true);
    let cancellationMessage = "Operation canceled due to new request.";

    if (cancelGetFlightPoliciesToken.current) {
      cancelGetFlightPoliciesToken.current.cancel(cancellationMessage);
    }

    cancelGetFlightPoliciesToken.current = axios.CancelToken.source();

    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/flight-policies`,
      data: payload,
      cancelToken: cancelGetFlightPoliciesToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            let policies = res?.data?.data?.data;
            if (payload?.flights?.at(0)?.pnr === "---") {
              policies = policies.filter(
                (policy) => policy?.supplier !== "Airhelp Plus"
              );
            }
            if (callback) {
              callback(policies);
            } else {
              dispatch(setPolicies(policies));
              getActualMinAndMaxInsurancePrice(policies);
            }
          }
        }
      })
      .catch((err) => {});
  };

  const bookInsurance = async ({ data, setLoading, insurance, callback }) => {
    const payload = parseInsuranceBookingData({ data, insurance });

    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/flight-purchase`,
      data: payload,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Protection booking successful", {
              variant: "success",
            });
            if (callback) {
              callback(res?.data?.data);
            } else {
              sessionStorage.setItem(
                `booking/${res?.data?.data?.purchasedInsurance?._id}`,
                JSON.stringify(removePassportPhoto(res))
              );
              navigate(
                `/protection/payment?${URLencode({
                  id: res?.data?.data?.purchasedInsurance?._id,
                  policy: insurance?.policyId,
                })}`
              );
            }
          } else {
            enqueueSnackbar(
              `Protection booking failed: ${
                res.data.message || "Unknown error"
              }`,
              {
                variant: "error",
              }
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Protection booking failed: Network error", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  function getBaseURL() {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    const baseURL = `${protocol}//${hostname}${port ? ":" + port : ""}`;
    return baseURL;
  }

  const initializeInsurancePayment = async ({
    setLoading,
    paymentType,
    savedCard,
    id,
    callback,
    callbackUrl,
  }) => {
    let parameters = {
      id,
    };

    const data = {
      insurancePurchaseId: id,
      paymentMode: paymentType,
      callbackUrl:
        callbackUrl ||
        `${getBaseURL()}/protection/confirmation?${URLencode(parameters)}`,
    };

    if (paymentType === "SavedCard" && savedCard) {
      data.savedCardId = savedCard;
    }

    setLoading && setLoading(true);

    await fetchServer({
      method: "POST",
      url: `/payment/v1/payment/insurance`,
      data,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            switch (paymentType) {
              case "SavedCard":
                if (callback) {
                  callback();
                } else {
                  navigate(
                    `/protection/confirmation?${URLencode(parameters)}`,
                    {
                      replace: true,
                    }
                  );
                }
                break;
              case "Card":
                if (res?.data?.data?.data?.authorization_url) {
                  if (callback) {
                    callback(res?.data?.data?.data?.authorization_url);
                  } else {
                    // navigate(`/cart/confirmation?${URLencode(parameters)}`);
                    window.open(res?.data?.data?.data?.authorization_url);
                    // window.location.replace(
                    //   res?.data?.data?.data?.authorization_url
                    // );
                  }
                }
                break;
              case "Wallet":
                if (callback) {
                  callback();
                } else {
                  navigate(
                    `/protection/confirmation?${URLencode(parameters)}`,
                    {
                      replace: true,
                    }
                  );
                }
                break;
              default:
                enqueueSnackbar("Payment initiated", {
                  variant: "success",
                });
                break;
            }
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              }
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const getInsuranceBooking = async ({
    component = false,
    lookForSaved = false,
    setLoading,
  }) => {
    const params = URLdecode();
    if (lookForSaved) {
      let savedOrder = sessionStorage.getItem(`booking/${params?.id}`);
      if (savedOrder) {
        return;
      }
    }

    dispatch(setLoadingModal(true));
    setLoading && setLoading(true);

    await fetchServer({
      method: "GET",
      url: `/product/v1/insurance/${params?.id}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const booking = res?.data?.data;
            let parameters = {
              id: booking?._id,
              status: booking?.status,
            };
            const updatedBooking = removePassportPhoto(res, true);
            sessionStorage.setItem(
              `booking/${params?.id}`,
              JSON.stringify(updatedBooking)
            );
            if (!component) {
              let url = `/protection/confirmation?${URLencode(parameters)}`;
              window.location.replace(url);
            }
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              }
            );
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          }
        }
      })
      .catch((err) => {
        // console.log("Error: ", err);
        // enqueueSnackbar("Something went wrong. please try again later.", {
        //   variant: "error",
        // });
      })
      .finally(() => {
        dispatch(setLoadingModal(false));
        setLoading && setLoading(false);
      });
  };

  const cancelAndRefundInsurance = async ({
    booking,
    setLoading,
    callback,
    noSave = false,
  }) => {
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/cancel-confirmed/${booking?._id}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            enqueueSnackbar("Ticket cancelled successfully", {
              variant: "success",
            });
            if (callback) {
              callback(bookingData);
            }
            if (!noSave) {
              sessionStorage.setItem(
                `booking/${booking?._id}`,
                JSON.stringify(bookingData)
              );
            }
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              }
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const getInsuranceBookings = async (params, setLoading) => {
    setLoading?.(true);
    let { limit = 0, offset = 0, searchBy, keyword, status } = params || {};
    const active = ticketType === configurations.products.insurance.value;
    switch (status) {
      case "Paid":
        status = "Paid";
        break;
      case "Pending":
        status = "Booked,Pending";
        break;
      case "Canceled":
        status = "Canceled,Cancelled";
        break;
      case "Completed":
        status = "Issued";
        break;
      case "Refunded":
        status = "Refunded";
        break;
      default:
        status = "";
        break;
    }
    let cancellationMessage = "Operation canceled due to new request.";

    if (cancelOrdersToken.current) {
      cancelOrdersToken.current.cancel(cancellationMessage);
    }

    cancelOrdersToken.current = axios.CancelToken.source();

    const response = await fetchServer({
      method: "GET",
      url: `/product/v1/insurance?limit=${limit}&offset=${offset}${
        searchBy && active
          ? `&searchBy=contactFirstName,bookingId,supplier,contactEmail,requestBody.name&keyword=${keyword}`
          : ""
      }${status && active ? `&filterBy=status&filterValue=${status}` : ""}`,
      cancelToken: cancelOrdersToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading?.(false);
        if (res) {
          if (res.status === 200) {
            return [
              Array.isArray(res.data.data?.data) ? res.data.data?.data : [],
              res?.data?.data?.meta?.total || 0,
            ];
          }
        }
      })
      .catch((err) => {});

    return response || [];
  };

  const getFlightOrderForInsurance = async (setLoading) => {
    const params = URLdecode();
    const savedBooking = sessionStorage.getItem(
      configurations.flightOrderSaveKey?.replace(":id", params?.flightId)
    );

    if (!params?.flightId) return;
    if (savedBooking) return;

    setLoading && setLoading(true);

    await fetchServer({
      method: "GET",
      url: `/product/v1/book/${params?.flightId}?populate=flightBooking`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const booking = res?.data?.data?.booking;
            sessionStorage.setItem(
              configurations.flightOrderSaveKey?.replace(":id", booking?._id),
              JSON.stringify(booking)
            );
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              }
            );
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          }
        }
      })
      .catch((err) => {})
      .finally(() => {
        setLoading && setLoading(false);
      });
  };

  const updateFarePrice = async ({
    booking,
    setLoading,
    callbackUrl,
    fare,
  }) => {
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/update-fare/${booking?._id}`,
      data: {
        fare,
      },
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            callbackUrl && callbackUrl(bookingData);
            enqueueSnackbar("Fare updated successfully", {
              variant: "success",
            });
          } else {
            // accommodationFareUpdateError({ data: res?.data });
            // enqueueSnackbar(
            //   res?.data?.message ||
            //     "Something went wrong. please try again later.",
            //   {
            //     variant: "error",
            //   }
            // );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  return {
    getFlightPolicies,
    bookInsurance,
    initializeInsurancePayment,
    getInsuranceBooking,
    cancelAndRefundInsurance,
    getInsuranceBookings,
    getFlightOrderForInsurance,
    updateFarePrice,
  };
}
